import { DashboardModal } from "dashboard/Dashboard";
import { tabTooltips } from "dashboard/_root/constants/tabTooltips";
import { useUserPanels } from "dashboard/_root/contexts/UserPanelsContext";
import { OnDragEndResponder } from "react-beautiful-dnd";
import { useHistory, useLocation } from "react-router";
import NavigationTabs from "_shared/components/NavigationTabs/NavigationTabs";
import { reorderArrayItem } from "_shared/helpers/arrayMethods";
import { stringToUrl } from "_shared/helpers/formatting";
import { useEffect, useRef } from "react";
import { analytics } from "_shared/utils/analytics";

interface Props {
  setOpenModal: (modal: DashboardModal) => void;
}

function DashboardTabs({ setOpenModal }: Props) {
  const location = useLocation();
  const path = location.pathname.split("/")[2];
  const history = useHistory();

  const { panels, updatePanels, hidePanel, renamePanel, removePanel } =
    useUserPanels();

  const onDragEnd: OnDragEndResponder = (result) => {
    if (!result.destination) return;
    const hiddenPanels = panels.filter((p) => p.hidden);
    const orderedPanels = reorderArrayItem(
      panels.filter((p) => !p.hidden), // only order visible panels
      result.source.index,
      result.destination.index,
    );
    updatePanels([
      ...orderedPanels.map((panel, i) => ({ id: panel.id, order: i })),
      ...hiddenPanels.map((panel, i) => ({
        // set order of hidden panels to be after visible panels
        id: panel.id,
        order: i + orderedPanels.length,
      })),
    ]);
  };

  const currentTabTimer = useRef<NodeJS.Timeout | null>(null);
  const lastTab = useRef<string | null>(null);

  useEffect(() => {
    if (currentTabTimer.current) clearTimeout(currentTabTimer.current);
    if (lastTab.current === path) return;
    lastTab.current = path;
    currentTabTimer.current = setTimeout(() => {
      analytics.track("Tab Viewed", { tab: path });
    }, analytics.tabTimeout);
  }, [path]);

  return (
    <div className="guidedTour9" style={{ width: "calc(100% - 100px)" }}>
      {[false, true].map((showLongbowPanels) => (
        <NavigationTabs
          onDragEnd={onDragEnd}
          contextItems={[
            { label: "Hide", action: (id) => hidePanel(id) },
            {
              label: "Create a new tab",
              action: () => setOpenModal("createTab"),
            },
          ]}
          data-testid="dashboard-tabs"
          className={"guidedTour1"}
        >
          {panels
            .filter(
              (tab) => !tab.hidden && !!tab.longbow_panel === showLongbowPanels,
            )
            .map((tab, index) => {
              const url = stringToUrl(tab.name);
              const tooltip = tabTooltips[tab.name as keyof typeof tabTooltips];
              const isLongbowTab = tab.longbow_panel;
              const customView = `/${localStorage.getItem("view")}-view`;

              return (
                <NavigationTabs.Tab
                  key={tab.id}
                  id={tab.id}
                  index={tab.order}
                  label={tab.name}
                  selected={path === url}
                  path={`/dashboard/${url}${customView}`}
                  tooltip={tooltip}
                  iconSrc={
                    isLongbowTab
                      ? tab.subscription_id
                        ? "/longbow_tabs.svg"
                        : "/market_insights_tab.svg"
                      : undefined
                  }
                  onRename={
                    isLongbowTab
                      ? undefined
                      : (id, name) => {
                          // redirect if on the panel we're renaming
                          if (path === url) {
                            history.push(`/dashboard/${stringToUrl(name)}`);
                          }
                          renamePanel(id, name);
                        }
                  }
                  contextItems={
                    isLongbowTab
                      ? []
                      : [
                          {
                            label: "Remove",
                            action: removePanel,
                          },
                        ]
                  }
                />
              );
            })}
        </NavigationTabs>
      ))}
    </div>
  );
}

export default DashboardTabs;
